import { X } from 'lucide-react'

import { emptyStepRow, type StepRow } from '@/components/editorRows'
import { SortableList } from '@/components/SortableList'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { fr } from '@/i18n/fr'

interface Props {
  rows: StepRow[]
  onChange: (rows: StepRow[]) => void
}

/**
 * Steps reorder the same way ingredients do, through the same `SortableList` (NFR-19), and
 * their handle sits on the **right** for the same reason — two sortable lists in one form
 * with grips on opposite sides is exactly the drift sharing the component was meant to
 * prevent. Steps keep a visible delete rather than an overflow menu: a step has no note and
 * no optional flag, so there is nothing for a menu to hold but the one control.
 *
 * The displayed number comes from the array index, which is also where `position` comes from
 * server-side (invariant 14) — one ordering, one source.
 */
export function StepEditor({ rows, onChange }: Props) {
  function update(id: string, text: string) {
    onChange(rows.map((row) => (row.id === id ? { ...row, text } : row)))
  }

  function remove(id: string) {
    onChange(rows.filter((row) => row.id !== id))
  }

  return (
    <div className="flex flex-col gap-3">
      <SortableList rows={rows} onReorder={onChange}>
        {(row, index, handle) => (
          <div className="flex items-start gap-2">
            <span className="mt-3 w-5 shrink-0 text-right text-sm font-semibold text-stone-400">
              {index + 1}.
            </span>
            <Textarea
              value={row.text}
              onChange={(event) => update(row.id, event.target.value)}
              placeholder={fr.recipeEdit.stepPlaceholder(index + 1)}
              aria-label={fr.recipeEdit.stepPlaceholder(index + 1)}
              rows={2}
              className="min-w-0 flex-1"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => remove(row.id)}
              aria-label={fr.common.remove}
              className="shrink-0 text-stone-400 hover:text-stone-900 dark:hover:text-stone-100"
            >
              <X className="h-4 w-4" />
            </Button>
            <div className="shrink-0">{handle}</div>
          </div>
        )}
      </SortableList>

      <Button type="button" variant="outline" onClick={() => onChange([...rows, emptyStepRow()])}>
        {fr.recipeEdit.addStep}
      </Button>
    </div>
  )
}
